<template>
  <Transition name="fade">
    <div
      v-if="!dismissed"
      class="flex items-stretch gap-4 overflow-hidden border border-grey-900/50 shadow-md"
      :class="[theme.classes, size === 'large' ? 'rounded-2xl' : 'rounded-lg']"
    >
      <div
        class="relative bg-gradient-to-b py-4"
        :class="[theme.gradient, size === 'large' ? 'pr-2 pl-4' : 'px-3']"
      >
        <component
          :is="customIcon ? customIcon : theme.icon"
          class="size-6 shrink-0"
        />
        <svg
          class="right absolute top-0 -right-4 h-full w-4"
          preserveAspectRatio="none"
          width="16"
          height="76"
          viewBox="0 0 16 76"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            :fill="'url(#' + type + 'curveGradient)'"
            d="M-4.76837e-06 1.39876e-06L16 2.79753e-06L14.7462 2.97787C5.01361 26.0927 -9.21992e-06 50.9198 -1.14125e-05 76L-16 76C-16 50.9198 -10.9864 26.0927 -1.25384 2.97786L-4.76837e-06 1.39876e-06Z"
          />
          <defs>
            <linearGradient
              :id="type + 'curveGradient'"
              x1="0%"
              y1="0%"
              x2="0%"
              y2="100%"
            >
              <stop offset="0%" :stop-color="theme.curve" />
              <stop offset="100%" :stop-color="theme.curve + '00'" />
            </linearGradient>
          </defs>
        </svg>
      </div>
      <div class="py-4 pr-4 pl-2">
        <div v-if="content?.heading && size === 'large'">
          <strong class="text-base font-semibold lg:text-lg">{{
            content.heading
          }}</strong>
        </div>
        <div
          class="font-light"
          :class="[
            size === 'large' ? 'text-base lg:text-lg' : 'text-xs lg:text-sm',
            {
              'mb-3': content?.link,
              'max-sm:mb-3': dismissible,
            },
          ]"
        >
          <span v-if="content?.isVhtml" v-html="content.body"></span>
          <span v-else>{{ content?.body }}</span>
          <slot />
        </div>
        <div
          v-if="content?.link || dismissible"
          class="flex flex-wrap gap-6 gap-y-3"
        >
          <NuxtLink
            v-if="content?.link"
            :to="content.link.url"
            class="link inline-flex items-center"
            :class="
              size === 'large'
                ? 'gap-1.5 text-lg'
                : 'gap-1 text-xs lg:gap-1.5 lg:text-sm'
            "
          >
            {{ content.link.label }}
            <IconOpenNewWindow
              v-if="content.link.offPage"
              class="fill-current"
              :class="size === 'large' ? 'size-4' : 'size-3 lg:size-4'"
            />
          </NuxtLink>

          <button
            v-if="dismissible"
            class="text-left font-normal text-rose-800 sm:hidden"
            :class="size === 'large' ? 'text-lg' : 'text-xs'"
            type="button"
            @click="dismissed = true"
          >
            Dismiss
          </button>
        </div>
      </div>
      <div v-if="dismissible" class="shrink-0 py-4 pr-4 max-sm:hidden">
        <button
          class="group flex size-6 place-items-center"
          type="button"
          @click="dismissed = true"
        >
          <span class="sr-only">Dismiss</span>
          <IconClose
            class="size-6 fill-grey-700 transition-colors group-hover:fill-grey-900"
          />
        </button>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
/**
 * ENHANCEMENTS TODO:
 * Add option for modal
 */

const props = defineProps<{
  type: AlertTypes;
  size: "small" | "large";
  customIcon?: Component | string;
  content?: {
    heading?: string;
    body?: string;
    link?: {
      url: string;
      label: string;
      offPage?: boolean;
    };
    isVhtml?: boolean;
  };
  dismissible?: boolean;
}>();

const dismissed = ref(false);

type AlertTypes = "warning" | "fatal" | "success" | "info" | "promo";

const themeMap: Record<
  AlertTypes,
  {
    icon: Component | string;
    classes: string;
    curve: string;
    gradient: string;
  }
> = {
  warning: {
    icon: resolveComponent("IconAlertWarning"),
    classes: "bg-lemon-50 fill-lemon-900",
    curve: "#FFFED3",
    gradient: "from-lemon-100 to-green-lemon/0",
  },
  fatal: {
    icon: resolveComponent("IconAlertFatal"),
    classes: "bg-rose-50 fill-rose-800",
    curve: "#FFE4E6",
    gradient: "from-rose-100 to-rose-100/0",
  },
  success: {
    icon: resolveComponent("IconAlertSuccess"),
    classes: "bg-green-50 fill-green-800",
    curve: "#E6FCD4",
    gradient: "from-green-100 to-green-100/0",
  },
  info: {
    icon: resolveComponent("IconAlertInfo"),
    classes: "bg-sky-50 fill-sky-800",
    curve: "#E0F2FE",
    gradient: "from-sky-100 to-sky-100/0",
  },
  promo: {
    icon: resolveComponent("IconAlertPromo"),
    classes: "bg-jag-50 fill-jag-800",
    curve: "#D3F4ED",
    gradient: "from-jag-100 to-jag-100/0",
  },
};

const theme = computed(() => themeMap[props.type]);
</script>
